import { useEffect, useRef, useCallback } from 'react';
import {
  trackFunnelEvent,
  trackPageView,
  trackCtaClick,
  trackScrollDepth,
  trackSectionView,
} from '../utils/funnel';
import {
  trackViewContent,
  trackLead,
  trackInitiateCheckout,
  trackPurchase,
  trackCompleteRegistration,
} from '../services/tracking';

export function usePageTracking(pageName: string, courseId?: number, coursePrice?: number) {
  useEffect(() => {
    trackPageView();
    if (courseId && coursePrice !== undefined) {
      trackViewContent(pageName, [String(courseId)], coursePrice);
    }
  }, [pageName, courseId, coursePrice]);
}

export function useScrollTracking() {
  const depths = useRef<Set<number>>(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = Math.round(
        ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
      );

      const thresholds = [25, 50, 75, 100];
      for (const threshold of thresholds) {
        if (scrollPercent >= threshold && !depths.current.has(threshold)) {
          depths.current.add(threshold);
          trackScrollDepth(threshold);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
}

export function useSectionTracking(sectionId: string, courseId?: number) {
  const ref = useRef<HTMLDivElement>(null);
  const viewed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewed.current) {
          viewed.current = true;
          trackSectionView(sectionId, courseId);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [sectionId, courseId]);

  return ref;
}

export function useCtaTracking(elementId: string, courseId?: number) {
  return useCallback(
    (metadata?: Record<string, any>) => {
      trackCtaClick(elementId, courseId, metadata);
    },
    [elementId, courseId]
  );
}

export function trackEnrollmentFunnel(
  step: 'start' | 'step_complete' | 'submit',
  data?: { step?: number; courseId?: number }
) {
  if (step === 'start') {
    trackFunnelEvent({
      event_type: 'enrollment_start',
      page_url: window.location.href,
      course_id: data?.courseId,
    });
  } else if (step === 'step_complete') {
    trackFunnelEvent({
      event_type: 'enrollment_step_complete',
      page_url: window.location.href,
      course_id: data?.courseId,
      metadata: { step: data?.step },
    });
  } else if (step === 'submit') {
    trackFunnelEvent({
      event_type: 'enrollment_submit',
      page_url: window.location.href,
      course_id: data?.courseId,
    });
    trackCompleteRegistration('Enrollment');
  }
}

export function trackPaymentFunnel(
  step: 'initiated' | 'started' | 'success' | 'failed' | 'abandoned',
  data: { courseId: number; amount: number; orderId?: string; reason?: string }
) {
  switch (step) {
    case 'initiated':
      trackInitiateCheckout('Course Enrollment', data.amount);
      trackFunnelEvent({
        event_type: 'checkout_initiated',
        page_url: window.location.href,
        course_id: data.courseId,
        metadata: { amount: data.amount },
      });
      break;
    case 'started':
      trackFunnelEvent({
        event_type: 'payment_started',
        page_url: window.location.href,
        course_id: data.courseId,
        metadata: { amount: data.amount },
      });
      break;
    case 'success':
      trackPurchase('Course Enrollment', data.amount, data.orderId ?? '');
      trackFunnelEvent({
        event_type: 'payment_success',
        page_url: window.location.href,
        course_id: data.courseId,
        metadata: { amount: data.amount, order_id: data.orderId },
      });
      break;
    case 'failed':
      trackFunnelEvent({
        event_type: 'payment_failed',
        page_url: window.location.href,
        course_id: data.courseId,
        metadata: { amount: data.amount, reason: data.reason },
      });
      break;
    case 'abandoned':
      trackFunnelEvent({
        event_type: 'checkout_abandoned',
        page_url: window.location.href,
        course_id: data.courseId,
        metadata: { amount: data.amount },
      });
      break;
  }
}

export function trackLeadFunnel(
  step: 'popup_shown' | 'popup_dismissed' | 'submit' | 'success',
  source: string,
  metadata?: Record<string, any>
) {
  const eventMap: Record<string, string> = {
    popup_shown: 'lead_popup_shown',
    popup_dismissed: 'lead_popup_dismissed',
    submit: 'lead_capture_submit',
    success: 'lead_capture_success',
  };

  trackFunnelEvent({
    event_type: eventMap[step],
    page_url: window.location.href,
    metadata: { source, ...metadata },
  });

  if (step === 'success') {
    trackLead(source);
  }
}
